import React, { useEffect, useMemo, useState } from 'react';

import { InsightsPageCmp, InsightsPageProps, SplashPageTypes } from './types';
import { Routes } from '../../navigation';
import { SplashPage } from './components/SplashPage';
import { fetchInsightsStatus } from 'services/insights/api';
import { ConversightStatus } from 'services/insights';
import { Page } from 'ui/components/Page/Page';
import { colorPalette } from 'ui/theme';
import { CircularProgress } from '@mui/material';
import { Box } from '@mui/system';
import { elapsedTime } from './helpers';
import { useSelector } from 'react-redux';
import { getActiveUser } from 'services/user';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Insights: InsightsPageCmp = (props: InsightsPageProps) => {
  const [conversightStatusInfo, setConversightStatusInfo] =
    useState<ConversightStatus | null>(null);

  const { user } = useSelector(getActiveUser);
  const currentUserEmail = useMemo(() => user?.email, [user]);

  const elapsed = useMemo(() => {
    if (conversightStatusInfo?.startTime) {
      return elapsedTime(conversightStatusInfo?.startTime);
    }
    return null;
  }, [conversightStatusInfo?.startTime]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const status = await fetchInsightsStatus();
        setConversightStatusInfo(status);
      } catch (error) {
        // Trigger an error to be displayed on the splash page
        setConversightStatusInfo({
          startTime: null,
          endTime: null,
          status: 'Error',
          users: currentUserEmail ? [currentUserEmail] : null,
          url: null,
          message:
            (error as any)?.response?.data?.message ||
            (error as any)?.data?.message ||
            (error as any)?.message ||
            'Error fetching insights status',
        });
        console.error(error);
      }
    };

    fetchStatus(); // Initial fetch
  }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const status = await fetchInsightsStatus();
        setConversightStatusInfo(status);
      } catch (error) {
        if (error instanceof Error) {
          // Trigger an error to be displayed on the splash page
          setConversightStatusInfo({
            startTime: null,
            endTime: null,
            status: 'Error',
            users: null,
            url: null,
            message:
              (error as any)?.response?.data?.message ||
              (error as any)?.data?.message ||
              (error as any)?.message ||
              'Error fetching insights status',
          });
        }

        console.error(error);
      }
    };

    // Clear existing interval
    let intervalId: NodeJS.Timeout | null = null;
    if (intervalId !== null) {
      clearInterval(intervalId);
    }

    // Set new interval if conditions are met
    if (
      conversightStatusInfo?.status !== 'Provisioned' &&
      conversightStatusInfo?.startTime &&
      elapsed !== null &&
      elapsed < 30
    ) {
      intervalId = setInterval(fetchStatus, 30000);
    }

    // Cleanup on unmount or when conversightStatusInfo changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [conversightStatusInfo]);

  const conversightStatus = useMemo(() => {
    if (!conversightStatusInfo) {
      return null;
    }

    if (
      currentUserEmail &&
      !conversightStatusInfo.users?.includes(currentUserEmail)
    ) {
      return SplashPageTypes.unauthorized;
    }

    switch (conversightStatusInfo.status) {
      case 'Provisioned':
        return 'provisioned';
      case 'Error':
        return SplashPageTypes.error;
      default:
        if (
          conversightStatusInfo.startTime &&
          !conversightStatusInfo.endTime &&
          elapsed &&
          elapsed < 30
        ) {
          return SplashPageTypes.provisioning;
        } else {
          return SplashPageTypes.error;
        }
    }
  }, [conversightStatusInfo, currentUserEmail, elapsed]);

  return conversightStatus === null ? (
    <Page
      margin="-16px"
      width="calc(100% + 32px);"
      height="calc(100% + 32px);"
      border="none"
    >
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress
          size={100}
          sx={{ color: colorPalette.redesign.lightPrimaryBlue }}
          data-qa="circular-spinner"
        />
      </Box>
    </Page>
  ) : conversightStatus === 'provisioned' && conversightStatusInfo?.url ? (
    <Page
      margin="-16px"
      width="calc(100% + 32px);"
      height="calc(100% + 32px);"
      border="none"
    >
      <Box
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <iframe
          src={conversightStatusInfo?.url}
          title="AI Insights"
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </Box>
    </Page>
  ) : conversightStatus === SplashPageTypes.error ||
    conversightStatus === SplashPageTypes.unauthorized ||
    conversightStatus === SplashPageTypes.provisioning ? (
    <SplashPage
      pageType={conversightStatus}
      statusMessage={conversightStatusInfo?.message ?? undefined}
    />
  ) : (
    <SplashPage
      pageType={SplashPageTypes.error}
      statusMessage={conversightStatusInfo?.message ?? undefined}
    />
  );
};

Insights.route = Routes.InsightsPage;

export default Insights;
